/**
 * TagSelector
 * Searchable dropdown for selecting tags + pills for selected/recently-touched tags.
 * - Dropdown opens on focus/type, shows matching tags (excludes already-visible pills)
 * - Selected tags render as green pills
 * - Unselected tags remain visible as grey pills so the user can re-toggle
 * - Quick-add option when search has no exact match
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';

type TagOption = {
  id: string;
  name: string;
  isActive?: boolean;
};

type TagSelectorProps = {
  tagOptions: TagOption[];
  selectedTagIds: string[];
  onToggle: (tagId: string) => void;
  onCreateTag?: (name: string) => Promise<{ id: string; name: string }>;
  inactiveSelectedTags?: TagOption[];
};

export function TagSelector({
  tagOptions,
  selectedTagIds,
  onToggle,
  onCreateTag,
  inactiveSelectedTags = [],
}: TagSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [visibleTagIds, setVisibleTagIds] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setVisibleTagIds((prev) => {
      const next = new Set(prev);
      selectedTagIds.forEach((id) => next.add(id));
      return next;
    });
  }, [selectedTagIds]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectFromDropdown = useCallback(
    (tagId: string) => {
      setVisibleTagIds((prev) => new Set(prev).add(tagId));
      if (!selectedTagIds.includes(tagId)) {
        onToggle(tagId);
      }
      setSearchTerm('');
      setIsOpen(false);
      inputRef.current?.focus();
    },
    [selectedTagIds, onToggle],
  );

  const handlePillClick = useCallback(
    (tagId: string) => {
      onToggle(tagId);
    },
    [onToggle],
  );

  const handleQuickAdd = async () => {
    if (!onCreateTag || !searchTerm.trim()) return;
    setIsCreating(true);
    try {
      const newTag = await onCreateTag(searchTerm.trim());
      setVisibleTagIds((prev) => new Set(prev).add(newTag.id));
      onToggle(newTag.id);
      setSearchTerm('');
      setIsOpen(false);
    } finally {
      setIsCreating(false);
    }
  };

  const dropdownCandidates = tagOptions.filter(
    (t) => !visibleTagIds.has(t.id) && !inactiveSelectedTags.some((it) => it.id === t.id),
  );

  const filteredDropdown = searchTerm.trim()
    ? dropdownCandidates.filter((t) =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : dropdownCandidates;

  const exactMatch = tagOptions.some(
    (t) => t.name.toLowerCase() === searchTerm.trim().toLowerCase(),
  );
  const showQuickAdd = onCreateTag && searchTerm.trim().length >= 2 && !exactMatch;

  const visibleTags = tagOptions.filter((t) => visibleTagIds.has(t.id));

  return (
    <div ref={containerRef}>
      <span className="block text-sm font-medium text-gray-700 mb-1">
        Tags{selectedTagIds.length > 0 && ` (${selectedTagIds.length})`}
      </span>

      {/* Search input / dropdown trigger */}
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search or create tags..."
          className="block w-full rounded-md border border-gray-300 pl-9 pr-3 py-1.5 text-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]"
        />

        {/* Dropdown */}
        {isOpen && (filteredDropdown.length > 0 || showQuickAdd) && (
          <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {filteredDropdown.slice(0, 20).map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleSelectFromDropdown(tag.id)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 truncate"
              >
                {tag.name}
              </button>
            ))}
            {filteredDropdown.length > 20 && (
              <p className="px-3 py-1.5 text-xs text-gray-400">
                {filteredDropdown.length - 20} more — keep typing to narrow down
              </p>
            )}
            {showQuickAdd && (
              <button
                type="button"
                onClick={handleQuickAdd}
                disabled={isCreating}
                className="w-full text-left px-3 py-1.5 text-sm text-[#026857] hover:bg-[#026857]/5 flex items-center gap-1.5 border-t border-gray-100 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                {isCreating ? 'Creating...' : `Create "${searchTerm.trim()}"`}
              </button>
            )}
          </div>
        )}

        {isOpen && searchTerm.trim().length >= 2 && filteredDropdown.length === 0 && !showQuickAdd && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg px-3 py-2 text-sm text-gray-500">
            No tags found
          </div>
        )}
      </div>

      {/* Visible pills: inactive system tags + selected/recently-touched tags */}
      <div className="flex flex-wrap gap-2">
        {inactiveSelectedTags.map((tag) => (
          <span
            key={tag.id}
            className="px-3 py-1 text-sm rounded-full border bg-gray-100 text-gray-500 border-gray-300 cursor-default"
            title="This tag is inactive and cannot be removed"
          >
            {tag.name} <span className="text-xs">(inactive)</span>
          </span>
        ))}

        {visibleTags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => handlePillClick(tag.id)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                isSelected
                  ? 'bg-[#026857] text-white border-[#026857]'
                  : 'bg-white text-gray-400 border-gray-300 hover:border-[#026857] hover:text-gray-600'
              }`}
            >
              {tag.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
